import React, { FC, useState, useEffect, Fragment, useMemo } from 'react';
import { map, find, get } from 'lodash';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import {
  CommodityType,
  Weld,
  CommodityFilterInput,
  CommodityList as Commodities,
  PagingInput,
  CommodityFilterByTerm,
  FilterOperator,
  CommodityComplexFilterInput,
  CommoditySortByTerm,
  SortOrder,
} from '@bechtel/types';
import { Box, IconSize, Text, Spinner, SpinnerSize, Button, Icon, ButtonSize } from '@bechtel/gds';
import { CommodityStatus, CommodityStatusLegend, SearchField, useGroupedCommodities } from '@bechtel/client-common';
import { GET_COMMODITIES } from '@bechtel/client-gql';
import { CommodityStatusActions } from '~/components/organisms/commodity-status-actions';
import CommodityItem from './commodities/item/internals';
import { TabButtonStyled, CommodityTypeListStyled, LoaderStyled } from './styled';
import { WeldComponent as WeldCommodity } from './commodities/weld';
import { TextFieldSize } from '@bechtel/gds';
import { Commodity } from './types';
import { t, Trans } from '@lingui/macro';

interface GetCommodityItemsParams {
  commodityType: CommodityType;
  commodities: Commodity[];
  collapseList: boolean[];
  setCollapseList: (newList: boolean[]) => void;
}

const CommodityItems: FC<GetCommodityItemsParams> = ({ commodityType, commodities, collapseList, setCollapseList }) => {
  if (commodities && commodities.length === 0) {
    return (
      <Box p={{ sm: '500' }} alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }}>
        <Text>
          <Trans>No commodities that match this criteria found.</Trans>
        </Text>
      </Box>
    );
  }
  // FIXME: this is pretty brittle since the commodity type name is DB driven
  switch (commodityType.name) {
    case 'Weld':
      return (
        <Fragment>
          {map(commodities, (comm: Weld, index: number) => {
            return (
              <WeldCommodity
                key={comm.id}
                commodity={comm}
                type={commodityType}
                collapsed={collapseList[index]}
                collapseMe={() => {
                  const newList = [...collapseList];
                  newList[index] = !newList[index];
                  setCollapseList(newList);
                }}
              />
            );
          })}
        </Fragment>
      );
    default:
      return (
        <Fragment>
          {map(commodities, (comm: Commodity, index: number) => {
            return (
              <CommodityItem
                key={comm.id}
                commodity={comm}
                collapsed={collapseList[index]}
                type={commodityType}
                collapseMe={() => {
                  const newList = [...collapseList];
                  newList[index] = !newList[index];
                  setCollapseList(newList);
                }}
              />
            );
          })}
        </Fragment>
      );
  }
};

interface CommodityListProps {
  workPackageId: string;
  availableCommodityTypes: CommodityType[];
  //commodities: CommodityWrapper[];
  scrollLength: number;
  resetScroll: () => void;
  pageSize: number;
  //groupedCommodities: GroupCommodities;
}

const CommodityList: FC<CommodityListProps> = ({
  workPackageId,
  availableCommodityTypes,
  scrollLength,
  resetScroll,
  pageSize,
}) => {
  const [selectedTypeId, setSelectedTypeId] = useState<string>();
  const [collapseAll, setCollapseAll] = useState<boolean>(false);
  const [searchCommodityQuery, setSearchCommodityQuery] = useState('');
  const [collapseList, setCollapseList] = useState<boolean[]>([]);
  const complexCommodityFilter: CommodityComplexFilterInput = {
    and: [
      { term: CommodityFilterByTerm.WORK_PACKAGE_ID, value: workPackageId, operator: FilterOperator.EQUALS },
      { term: CommodityFilterByTerm.COMMODITY_TYPE_ID, value: selectedTypeId, operator: FilterOperator.EQUALS },
      { term: CommodityFilterByTerm.EXCLUDE_CONNECTED_COMMODITY, value: 'true', operator: FilterOperator.EQUALS },
    ],
  };

  if (searchCommodityQuery) {
    complexCommodityFilter.and?.push({
      term: CommodityFilterByTerm.TAG_NUMBER,
      value: searchCommodityQuery.trim(),
      operator: FilterOperator.CONTAINS,
    });
  }

  const [getCommodities, { loading, error, data, refetch, networkStatus, fetchMore }] = useLazyQuery<
    { commodityList: Commodities },
    {
      input: CommodityFilterInput;
      paging: PagingInput;
    }
  >(GET_COMMODITIES, {
    variables: {
      input: {
        complexFilter: complexCommodityFilter,
        sortByFilter: {
          term: CommoditySortByTerm.TAG,
          sortOrder: SortOrder.ASC,
        },
      },
      paging: { cursor: '1', limit: pageSize },
    },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!availableCommodityTypes || availableCommodityTypes.length === 0) return;
    setSelectedTypeId(availableCommodityTypes[0].id);
  }, [availableCommodityTypes]);

  useEffect(() => {
    if (!selectedTypeId) return;
    getCommodities();
  }, [selectedTypeId]);

  const commodities = useMemo(() => get(data, 'commodities.items', []), [data]);
  const totalCommodities = get(data, 'commodities.total', 0);
  const cursor = get(data, 'commodities.cursor');

  useEffect(() => {
    if (scrollLength !== pageSize && commodities?.length < totalCommodities) {
      fetchMore({
        variables: {
          paging: { cursor: cursor, limit: pageSize },
        },
      });
    }
  }, [fetchMore, scrollLength, totalCommodities, pageSize, commodities?.length, cursor]);

  useEffect(() => {
    setCollapseList(Array(commodities.length).fill(false));
    setSelectedTypeId[availableCommodityTypes[0].id];
  }, [commodities, availableCommodityTypes]);

  const groupedCommodities = useGroupedCommodities(commodities);

  const selectedCommodityType =
    find(availableCommodityTypes, (commodityType) => commodityType.id === selectedTypeId) || availableCommodityTypes[0];

  const filteredCommodities = groupedCommodities[selectedCommodityType?.id];

  const milestones = filteredCommodities && filteredCommodities?.length > 0 ? filteredCommodities[0].milestones : [];

  if (error) {
    return (
      <Box css="height: 100%;">
        <LoaderStyled>
          <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
            <Text>
              <Trans>There was a problem loading the Commodities.</Trans>
            </Text>
            <Text>
              <Trans>Please click the button below to try again.</Trans>
            </Text>
          </Box>
          <Box>
            <Button size={ButtonSize.LARGE} onClick={() => refetch()}>
              <Box mr={{ sm: '400' }}>
                <Icon icon="undo" />
              </Box>
              Retry
            </Button>
          </Box>
        </LoaderStyled>
      </Box>
    );
  }

  return (
    <>
      <CommodityTypeListStyled>
        {map(availableCommodityTypes, (commType: CommodityType) => {
          return (
            <TabButtonStyled
              selected={commType.id === selectedCommodityType.id}
              onClick={() => {
                resetScroll();
                setSelectedTypeId(commType.id);
              }}
              key={commType.id}
            >
              {commType?.name}
            </TabButtonStyled>
          );
        })}
      </CommodityTypeListStyled>
      <Box flexDirection={{ sm: 'column' }}>
        <CommodityStatus>
          {/* FIXME : Brittle way of identifying weld */}
          <CommodityStatusLegend isWeldCommodity={selectedCommodityType.name === 'Weld'}></CommodityStatusLegend>
          <Box>
            <Box mr={{ sm: '300' }}>
              <SearchField
                size={TextFieldSize.SMALL}
                placeholder={t`Search by tag`}
                iconSize={IconSize.MEDIUM}
                type="search"
                onChange={(e) => setSearchCommodityQuery(e.target.value)}
                value={searchCommodityQuery}
              />
            </Box>
            <CommodityStatusActions
              commodityType={selectedCommodityType}
              setCollapseAll={() => {
                setCollapseAll(!collapseAll);
                setCollapseList(Array(collapseList?.length).fill(!collapseAll));
              }}
              collapseAll={collapseAll}
              milestones={milestones}
              commodities={groupedCommodities[selectedCommodityType.id]}
            />
          </Box>
        </CommodityStatus>
        <Box
          flexDirection={{ sm: 'column' }}
          p={{ sm: '400' }}
          pt={{ sm: '500' }}
          css={`
            ${({ theme }) => `background-color: ${theme.colors.grey[200]};`}
          `}
        >
          <CommodityItems
            commodityType={selectedCommodityType}
            commodities={filteredCommodities}
            collapseList={collapseList}
            setCollapseList={setCollapseList}
          />
        </Box>
      </Box>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box py={{ sm: '500' }} flexGrow={{ sm: '1' }} justifyContent={{ sm: 'center' }} data-testid="loading">
          <Spinner size={SpinnerSize.LARGE}></Spinner>
        </Box>
      )}
    </>
  );
};

export default CommodityList;
